import { NotificationPosition } from 'core/enums';
import { useAgGridClientTableContext } from 'core/states';
import { CorporatePartAssignVendor, CorporatePartAssignVendorFormData } from 'corporate/components';
import { isEditable } from 'quotes/components/CustomerQuotePartList/utils';
import { useQuotePartActions } from 'quotes/hooks';
import { useCustomerQuotePartsContext, useSelectedQuotePartContext } from 'quotes/states';

import { mapQuotePartAssignVendorFormData } from './mappers';
import { getDefaultValues, getExternalSupplier } from './utils';

interface IProps {
  isOpen: boolean;
  onClose: (closeSelection?: boolean) => void;
  onSubmitSuccess: () => void;
  interactive?: boolean;
  isAlternateVersionEnabled?: boolean;
}

export const QuotePartAssignVendor = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  interactive = false,
  isAlternateVersionEnabled = false,
}: IProps) => {
  const { currentQuoteVersion } = useCustomerQuotePartsContext();
  const { selectedRows, updateRows } = useAgGridClientTableContext();
  const { selectedQuotePart: inlineDrawerSelectedQuotePart } = useSelectedQuotePartContext();

  const selectedQuotePart =
    inlineDrawerSelectedQuotePart ?? (selectedRows.length === 1 ? selectedRows[0] : undefined);

  // For inline drawer, we need to check if the quote part is editable (drawer available also in
  // release mode, read only)
  // For selection action, we enabled the drawer only in draft mode
  const isRowEditable = selectedQuotePart
    ? isEditable({ data: selectedQuotePart, currentQuoteVersion })
    : true;

  const { upsertQuoteParts } = useQuotePartActions({
    notificationPosition: NotificationPosition.FORM,
  });

  const onSubmit = async (data: CorporatePartAssignVendorFormData) => {
    handleOnClose(true);

    const { success, quoteParts } = await upsertQuoteParts(
      mapQuotePartAssignVendorFormData(
        selectedQuotePart ? [selectedQuotePart] : selectedRows,
        data,
        isAlternateVersionEnabled
      ),
      { onSuccess: onSubmitSuccess }
    );

    if (!success) {
      return;
    }

    updateRows({ update: quoteParts }, { refreshCells: true });

    return { success: true };
  };

  const handleOnClose = (closeSelection = false) => {
    onClose(closeSelection);
  };

  return (
    <CorporatePartAssignVendor
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      defaultValues={getDefaultValues(selectedQuotePart, isAlternateVersionEnabled)}
      externalSupplier={getExternalSupplier(selectedQuotePart)}
      isEditable={isRowEditable}
      interactive={interactive}
      isAlternateVersionEnabled={isAlternateVersionEnabled}
    />
  );
};
